import React, { type ReactNode, useCallback, useEffect, useMemo } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { AIAnswerAPIType, type BookmarkState, isBookmarkIntent, JiraIssueSmartCard, type SearchAIAnswerQueryVariables, SearchAIDialog } from '@atlassian/search-ai';
import { Bookmark, BookmarkSuggestionCard } from '@atlassian/search-ai/bookmark';
import {
	useAdminPermissions,
	useBookmarkQuery,
	type UseBookmarkQueryResult,
} from '@atlassian/search-ai/bookmark-api';
import { AiBrand, CardTemplate, QueryIntent, SEARCH_AI_ANSWER_CACHE_KEY } from '@atlassian/search-ai/constants';
import { sainStreamFetchConvoAIService } from '@atlassian/search-ai/stream-fetch';
import { useIsSearchQueryPopular } from '@atlassian/search-ai/use-is-search-query-popular';
import { deleteSessionItem, getSessionItem } from '@atlassian/search-dialog';
import {
	getBackendExperiment,
	STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN,
} from '@atlassian/search-experiment';

import { EVENT_SOURCE } from '../../../common/constants/analytics';
import { ApplicationModes } from '../../../common/constants/application-modes';
import { MapProductToKnowledgeSources, ProductKeys } from '../../../common/constants/products';
import { SEARCH_DIALOG_AI_DIALOG_REFERER_KEY } from '../../../common/constants/quick-find';
import { ResultsStates } from '../../../common/constants/results';
import { UserStatus } from '../../../common/constants/types';
import { useAppContext } from '../../../common/ui/app-context';
import { isHello } from '../../../common/utils/is-hello';
import { usePrevious } from '../../../common/utils/use-previous/use-previous';
import { useSafeIntercom } from '../../../common/utils/use-safe-intercom';
import { useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import { useAreLLMPoweredFeaturesEnabled } from '../../../controllers/entitlement-checks';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useSelectedFilters } from '../../../controllers/filters/selected';
import { useOAuthHandlers } from '../../../controllers/oauth';
import { useQueryIntent } from '../../../controllers/query-intent';
import { useSmartAnswersIntentStore } from '../../../controllers/smart-answers-intent-store';
import { useApplicationMode, useBootstrap, useSearchSessionId } from '../../../controllers/store/bootstrap';
import { usePrimaryProduct, useUser } from '../../../controllers/store/bootstrap/accessors';
import { useResults, useResultsError, useResultsState, useSearchQuery, useSelectedProduct } from '../../../controllers/store/search';

import { SAIN_ENTITIES } from './constants';
import { getStoreMessageBodyParam, getStoreMessageHeader } from './utils/get-store-message-header';
import {
	mapFiltersToAggregator,
	mapSelectedFiltersToAggregator,
} from './utils/map-filters-to-aggregator';

const wrapperStyles = cssMap({
	root: {
		'&:empty': {
			display: 'none',
		},
	},
	wrapper: {
		width: '100%',
		marginTop: token('space.025'),
		'&:empty': {
			display: 'none',
		},
	},
});

const smartAnswersPortalStyles = cssMap({
	root: {
		'&:empty': {
			display: 'none',
		},
	},
	hidden: {
		display: 'none',
	},
});

type AiSearchStreamingFunctionParams = {
	variables: Omit<SearchAIAnswerQueryVariables, 'cloudIdARI'>;
};

const handleNavigateDummyFunction = () => { };

const SmartAnswerDialogWrapper = ({ children }: { children?: ReactNode }) => (
	<Box xcss={wrapperStyles.wrapper}>{children}</Box>
);

export type SmartAnswersProps = {
	isRelayEnabled?: boolean;
	resultsState?: ResultsStates;
	smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
};

/**
 * Get the AiBrand based on Rovo edition, ai enablement and application mode.
 */
export const getShowChatFollowUps = ({
	isRovoLLMEnabled,
	applicationMode,
}: {
	isRovoLLMEnabled?: boolean;
	applicationMode?: ApplicationModes;
}) => {
	if (fg('rovo_search_updated_entitlement_checks_for_team_25')) {
		if (applicationMode === ApplicationModes.Unified) {
			return false;
		}
		return isRovoLLMEnabled;
	}
	return applicationMode === ApplicationModes.Rovo;
};

export const SmartAnswers = (props: SmartAnswersProps): React.JSX.Element | null => {
	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	const [
		{
			cloudId,
			user,
			orgId,
			SAINHostName,
			analyticsAttributes,
			experimentConfig,
			productEdition,
			productDataRegions,
			isJiraIssueIndexed,
			thirdPartyConfigs,
		},
	] = useBootstrap();
	const [searchSessionId] = useSearchSessionId();
	const [query, { setSearchQuery }] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const { hasFiltersSelected, selectedFilters } = useSelectedFilters({});
	const applicationMode = useApplicationMode();
	const [selectedProduct] = useSelectedProduct();
	const mappedProductFilter = MapProductToKnowledgeSources[selectedProduct];
	const [previousMappedProductFilter] = usePrevious(mappedProductFilter, { count: 1 });
	const {
		commonAttributes: { has3pProducts, is3pSearch, mode, availableProducts },
	} = useCommonAndPrivateAttributes();
	const {
		clearStore: clearSearchResultStore,
		setQueryIntentClassificationData,
		setWorkspaceId,
	} = useSmartAnswersIntentStore();
	const intercom = useSafeIntercom();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [results] = useResults();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [error] = useResultsError();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();
	let resultsLoaded = resultsState === ResultsStates.RESULTS;

	if (props.isRelayEnabled) {
		resultsLoaded = props?.resultsState === ResultsStates.RESULTS;
	}

	const {
		loading: queryIntentLoading,
		transformedQuery,
		queryIntent,
		queryIntentClassificationData,
		requestId: queryIntentRequestId,
		traceId: queryIntentTraceId,
	} = useQueryIntent();
	const cloudIdARI = `ari:cloud:confluence::site/${cloudId}`;
	const workspaceId = analyticsAttributes?.activationId
		? `ari:cloud:${primaryProduct}:${cloudId}:workspace/${analyticsAttributes.activationId}`
		: '';
	const xProduct = applicationMode === ApplicationModes.Rovo ? 'rovo' : undefined;

	// Get dialog referrer tracking from sessionStorage
	const dialogReferrer = getSessionItem(SEARCH_DIALOG_AI_DIALOG_REFERER_KEY);

	// Clear the dialog referrer tracking after analytics events are fired
	const handleAnalyticsFired = useCallback(() => {
		// Clear the dialog referrer from sessionStorage
		deleteSessionItem(SEARCH_DIALOG_AI_DIALOG_REFERER_KEY);
	}, []);

	let showBookmark: boolean = false;
	let bookmarkResult: Omit<UseBookmarkQueryResult, 'refetch'> = {
		bookmarkState: 'ACTIVE' as BookmarkState,
		url: '',
		loading: false,
		id: '',
		isError: false,
		title: '',
		description: undefined,
		lastModifierAccountId: '',
		keyPhrase: '',
		parentAdminhubBookmarkId: '',
		requestId: null,
	};

	// consume the bookmark data from the intent API instead of calling the bookmark API,
	// when the SAIN nav card is enabled
	if (fg('kd_enable_sain_hero_nav')) {
		if (queryIntentClassificationData && isBookmarkIntent(queryIntentClassificationData)) {
			const { bookmark } = queryIntentClassificationData;
			bookmarkResult = {
				bookmarkState: bookmark?.bookmarkState as BookmarkState,
				url: bookmark?.url,
				loading: false,
				id: bookmark?.id,
				isError: false,
				title: bookmark?.title,
				description: bookmark?.description,
				lastModifierAccountId: bookmark?.lastModifierAccountId,
				keyPhrase: bookmark?.keyPhrase ?? '',
				parentAdminhubBookmarkId: bookmark?.parentAdminhubBookmarkId ?? '',
				requestId: queryIntentRequestId ?? null,
			};
		}
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const bookmarkData = useBookmarkQuery({
			cloudId: cloudId || '',
			query,
			workspaceId,
			product: primaryProduct || '',
			applicationMode,
			isRovoLLMEnabled,
		});
		bookmarkResult = {
			...bookmarkData,
		};
	}

	const { hasBookmarkCreationAccess, hasAdminhubAccess } = useAdminPermissions({
		cloudId: cloudId || '',
		orgId: orgId || '',
	});
	const {
		isPopular: isSearchQueryPopular,
		loading: isSearchQueryPopularLoading,
		error: isSearchQueryPopularError,
	} = useIsSearchQueryPopular({
		cloudId: cloudId ?? '',
		searchQuery: query,
		isEnabled: fg('kd_user_created_bookmarks'),
	});

	// if SAIN nav card is enabled, we show the nav card if the query intent is NAV_CONTENT or NAVIGATIONAL
	// for NAVIGATIONAL intents, we use the nav boost score from the FPS search results, which is checked inside the store
	const showNavCard =
		(queryIntent === QueryIntent.NAV_CONTENT || queryIntent === QueryIntent.NAVIGATIONAL) &&
		fg('kd_enable_sain_hero_nav');

	const parsedFilters = fg('kd_add_common_filters_support_to_sain')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
		useMemo(() => mapSelectedFiltersToAggregator(selectedFilters), [selectedFilters])
		: // eslint-disable-next-line react-hooks/rules-of-hooks
		useMemo(
			() => mapFiltersToAggregator(selectedFilters.confluenceFilters),
			[selectedFilters.confluenceFilters],
		);

	useEffect(
		() => {
			// clear the search result store when the query changes or component unmounts
			// this prevents stale nav card data from persisting when transitioning between intents
			fg('kd_enable_sain_hero_nav') && clearSearchResultStore();
			return () => {
				fg('kd_enable_sain_hero_nav') && clearSearchResultStore();
			};
		},
		[query, clearSearchResultStore],
	);

	if (fg('kd_hide_sain_for_unsupported_product_filters')) {
		// fg check is stable between renders and won't cause issues with hook calls
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (previousMappedProductFilter && previousMappedProductFilter !== mappedProductFilter) {
				deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
			}
		}, [previousMappedProductFilter, mappedProductFilter]);
	}

	const tracingHeaders = useMemo(() => {
		if (!fg('kd_sain_pass_traceid')) {
			return undefined;
		}
		// Use trace headers from intent detection, use arbitraty span id since its needed to propagate trace id
		if (queryIntentTraceId) {
			const spanId = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
			return {
				'X-B3-TraceId': queryIntentTraceId,
				'X-B3-SpanId': spanId,
			};
		}
		return undefined;
	}, [queryIntentTraceId]);
	const requestId = tracingHeaders?.['X-B3-TraceId'];

	// A callback needed to parse the streaming response, ideally it should be defined inside @atlassian/search-ai
	const aiSearchStreamingFunction = useCallback(
		({ variables }: AiSearchStreamingFunctionParams) => {
			return {
				type: AIAnswerAPIType.STREAM,
				stream: sainStreamFetchConvoAIService({
					variables: { ...variables, cloudIdARI },
					fetchConfig: {
						hostName: SAINHostName,
						credentials: 'include',
						headers: {
							'X-Cloudid': cloudId || '',
							...(xProduct ? { 'X-Product': xProduct } : {}),
							...(tracingHeaders ?? {}),
							...getStoreMessageHeader(),
						},
					},
					storeMessage: getStoreMessageBodyParam(),
					isReadingAids: false,
				}),
			};
		},
		[SAINHostName, cloudIdARI, cloudId, xProduct, tracingHeaders],
	);

	const backendExperiment = useMemo(
		() =>
			getBackendExperiment({
				experimentConfig,
				entities: SAIN_ENTITIES,
				productEdition,
				isHello: isHello(cloudId),
				productDataRegions,
				experience: 'sain',
				tenantId: cloudId,
				isJiraIssueIndexed,
				thirdPartyFilters: selectedFilters.thirdPartyFilters,
			}),
		[
			experimentConfig,
			productEdition,
			cloudId,
			productDataRegions,
			isJiraIssueIndexed,
			selectedFilters,
		],
	);
	const { experimentLayers } = backendExperiment || {};

	const extraAnalyticsAttributes = useMemo(
		() => ({
			isFullPageSearch: true,
			has3pProducts,
			is3pSearch,
			mode,
			availableProducts,
			referrer: dialogReferrer,
			originalQuery: query,
			transformedQuery: transformedQuery,
			requestId,
		}),
		[
			has3pProducts,
			is3pSearch,
			mode,
			availableProducts,
			dialogReferrer,
			query,
			transformedQuery,
			requestId,
		],
	);

	const intercomCallbacks = useMemo(
		() => ({
			onAIAnswerSourceClick: () => intercom?.trackEvent('ai_answer_source_clicked'),
			onFollowUpSuggestionClick: () => intercom?.trackEvent('ai_follow_up_suggestion_clicked'),
			onAIAnswerAnalyticsFired: () => handleAnalyticsFired(),
		}),
		[intercom, handleAnalyticsFired],
	);

	const userDetail = useMemo(() => ({ id: user?.id || '', name: user?.name || '' }), [user]);

	if (
		props.isRelayEnabled &&
		(props.resultsState === ResultsStates.NO_RESULTS || props.resultsState === ResultsStates.ERROR)
	) {
		return null;
	} else if (error && !results) {
		// Do not show smart answers when there are errors, or empty results
		return null;
	}

	// Do not show smart answers when the required data is not available
	if (!cloudId || !query) {
		return null;
	}

	// Do not show smart answers or bookmarks if query intent isn't set or is loading
	if (queryIntent === QueryIntent.DEFAULT || queryIntentLoading) {
		// If loading, register a UFO hold
		if (queryIntentLoading) {
			return <UFOLoadHold name="query-intent" />;
		}
		return null;
	}

	// if the SAIN nav card is enabled,
	// we rely on the intent detection API to determine if we should show the bookmark component
	// this prevents both the nav card and bookmark from being shown at the same time,
	// since there's a high overlap for these two components
	if (fg('kd_enable_sain_hero_nav')) {
		showBookmark = Boolean(
			queryIntent === QueryIntent.BOOKMARK &&
			queryIntentClassificationData &&
			isBookmarkIntent(queryIntentClassificationData) &&
			!selectedProduct &&
			resultsLoaded,
		);
	} else {
		showBookmark =
			!bookmarkResult.loading &&
			!bookmarkResult.isError &&
			queryIntent !== QueryIntent.PERSON &&
			!selectedProduct &&
			resultsLoaded;
	}

	if (showNavCard && queryIntentClassificationData) {
		setWorkspaceId(workspaceId ?? '');
		setQueryIntentClassificationData(queryIntentClassificationData);
	}

	const showPopularQuerySuggestion =
		isSearchQueryPopular &&
		!isSearchQueryPopularLoading &&
		!isSearchQueryPopularError &&
		showBookmark &&
		(queryIntent === QueryIntent.NAVIGATIONAL || queryIntent === QueryIntent.KEYWORD_OR_ACRONYM) &&
		fg('kd_user_created_bookmarks');

	const aiBrand = applicationMode === ApplicationModes.Rovo ? AiBrand.ROVO : AiBrand.AI;

	const hideTopicCard = queryIntent === QueryIntent.TOPIC && !!bookmarkResult.url;

	// No need to check for loaded results before showing SAIN (decision made by PM and team)
	const showSmartAnswer =
		queryIntent !== QueryIntent.NONE &&
		queryIntent !== QueryIntent.NAVIGATIONAL &&
		queryIntent !== QueryIntent.NAV_CONTENT &&
		queryIntent !== QueryIntent.BOOKMARK &&
		!bookmarkResult.loading &&
		!hideTopicCard &&
		(fg('kd_hide_sain_for_unsupported_product_filters')
			? // check either if no product filter is selected or a supported product filter is selected
			(!selectedProduct || mappedProductFilter !== undefined) && !hasFiltersSelected
			: true);

	// Show Jira Issue Card with priority for JIRA_ISSUE query intent
	if (queryIntent === QueryIntent.JIRA_ISSUE) {
		return (
			<UFOSegment name="search-ai-dialog">
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="hasSAINComponent" value={true} />
						<UFOCustomCohortData dataKey="sainExperienceName" value="Jira Issue Card" />
					</>
				) : (
					<UFOCustomData data={{ hasSAINComponent: true, sainExperienceName: 'Jira Issue Card' }} />
				)}
				<SmartAnswerDialogWrapper>
					<JiraIssueSmartCard
						issueKeyQuery={query}
						cloudId={cloudId}
						aiBrand={aiBrand}
						searchSessionId={searchSessionId}
						workspaceId={workspaceId}
						product={primaryProduct ?? ''}
						source={EVENT_SOURCE}
					/>
				</SmartAnswerDialogWrapper>
			</UFOSegment>
		);
	}

	// If a bookmark exists & can be shown, prioritize showing bookmark only
	// If a bookmark doesn't exist & query isn't answerable by SAIN, show the bookmark component to admins
	if (
		(showBookmark && !!bookmarkResult.url) ||
		(!showSmartAnswer && showBookmark && hasBookmarkCreationAccess)
	) {
		return (
			<UFOSegment name="search-ai-dialog">
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="hasSAINComponent" value={true} />
						<UFOCustomCohortData dataKey="sainExperienceName" value="Bookmark" />
					</>
				) : (
					<UFOCustomData data={{ hasSAINComponent: true, sainExperienceName: 'Bookmark' }} />
				)}
				<Bookmark
					query={query}
					cloudId={cloudId}
					bookmarkId={bookmarkResult.id}
					bookmarkState={bookmarkResult.bookmarkState}
					url={bookmarkResult.url}
					orgId={orgId || ''}
					searchSessionId={searchSessionId}
					source={EVENT_SOURCE}
					brand={aiBrand}
					title={bookmarkResult.title || ''}
					description={bookmarkResult.description || ''}
					hasAdminhubAccess={hasAdminhubAccess}
					hasBookmarkCreationAccess={hasBookmarkCreationAccess}
					userDetails={userDetail}
					product={primaryProduct}
					workspaceId={workspaceId}
					lastModifierAccountId={bookmarkResult.lastModifierAccountId}
					keyPhrase={bookmarkResult.keyPhrase}
					parentAdminhubBookmarkId={bookmarkResult.parentAdminhubBookmarkId}
					requestId={bookmarkResult.requestId}
				/>
			</UFOSegment>
		);
	}

	if (showPopularQuerySuggestion) {
		return (
			<BookmarkSuggestionCard
				suggestedTerm={query}
				cloudId={cloudId}
				orgId={orgId ?? ''}
				hasAdminhubAccess={hasAdminhubAccess}
				product={primaryProduct ?? 'unknown'}
				workspaceId={workspaceId}
				userDetails={userDetail}
				searchSessionId={searchSessionId}
				source={EVENT_SOURCE}
			/>
		);
	}

	// Otherwise if SAIN can answer a query, show the SAIN component
	else if (showSmartAnswer) {
		return (
			<>
				<SAINIntercomWrapper>
					{showBookmark && hasBookmarkCreationAccess ? (
						// If bookmark *could* be shown but doesn't exist, show the bookmark component to admins
						<Bookmark
							query={query}
							cloudId={cloudId}
							bookmarkId={bookmarkResult.id}
							url={bookmarkResult.url}
							orgId={orgId || ''}
							searchSessionId={searchSessionId}
							source={EVENT_SOURCE}
							brand={aiBrand}
							title={bookmarkResult.title || ''}
							description={bookmarkResult.description || ''}
							hasAdminhubAccess={hasAdminhubAccess}
							hasBookmarkCreationAccess={hasBookmarkCreationAccess}
							product={primaryProduct}
							workspaceId={workspaceId}
							lastModifierAccountId={bookmarkResult.lastModifierAccountId}
							keyPhrase={bookmarkResult.keyPhrase}
							parentAdminhubBookmarkId={bookmarkResult.parentAdminhubBookmarkId}
							requestId={bookmarkResult.requestId}
						/>
					) : null}
					<SearchAIDialog
						query={transformedQuery || query}
						cloudId={cloudId}
						orgId={orgId}
						getAIAnswer={aiSearchStreamingFunction}
						onNavigate={handleNavigateDummyFunction}
						userDetails={userDetail} // to personalize experience (even though it's not sent to backend)
						source={EVENT_SOURCE} // used for analytic events
						extraAnalyticsAttributes={extraAnalyticsAttributes}
						wrapper={SmartAnswerDialogWrapper}
						loadingWrapper={SmartAnswerDialogWrapper}
						setQuery={setSearchQuery}
						followUpsEnabled={true}
						searchSessionId={searchSessionId}
						selectedFilters={selectedFilters}
						parsedFilters={parsedFilters}
						selectedProduct={selectedProduct}
						mappedProductFilter={mappedProductFilter}
						smartAnswersRoute={queryIntent}
						cardTemplate={CardTemplate.SMART_ANSWER}
						baseQuery={query}
						brand={aiBrand}
						callbacks={intercomCallbacks}
						workspaceId={workspaceId}
						isAdminHubAIEnabled={isAdminHubAIEnabled}
						sourceProduct={primaryProduct}
						showChatFollowUps={getShowChatFollowUps({ isRovoLLMEnabled, applicationMode })}
						queryIntentClassificationData={queryIntentClassificationData}
						{...(fg('kd_sain_backend_exp_context')
							? {
								experimentContext: {
									// The experiment layers are mapped to ensure permitted attributes are sent into search requests.
									experimentLayers: (experimentLayers || []).map(
										({ name, layerId, shadowId, definitions }) => ({
											name,
											layerId,
											shadowId,
											definitions,
										}),
									),
								},
							}
							: {})}
						referrer={dialogReferrer}
						useOAuthHandlers={useOAuthHandlers}
						thirdPartyConfigs={thirdPartyConfigs}
						traceHeaders={tracingHeaders}
					/>
				</SAINIntercomWrapper>
			</>
		);
	}

	return null;
};

export const StandaloneBookmark = (props: SmartAnswersProps): React.JSX.Element | null => {
	const { isRovoLLMEnabled } = useAppContext();

	const [{ cloudId, user, orgId, analyticsAttributes }] = useBootstrap();
	const [searchSessionId] = useSearchSessionId();
	const [query] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const applicationMode = useApplicationMode();
	const workspaceId = analyticsAttributes?.activationId
		? `ari:cloud:${primaryProduct}:${cloudId}:workspace/${analyticsAttributes.activationId}`
		: '';

	const [selectedProduct] = useSelectedProduct();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [results] = useResults();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [error] = useResultsError();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();
	let resultsLoaded = resultsState === ResultsStates.RESULTS;

	if (props.isRelayEnabled) {
		resultsLoaded = props.resultsState === ResultsStates.RESULTS;
	}

	const {
		bookmarkState,
		url: bookmarkUrl,
		loading: bookmarkLoading,
		id: bookmarkId,
		isError,
		title,
		description,
		lastModifierAccountId,
		keyPhrase,
		parentAdminhubBookmarkId,
		requestId: bookmarkRequestId,
	} = useBookmarkQuery({
		cloudId: cloudId || '',
		query,
		workspaceId,
		product: primaryProduct || '',
		isStandaloneBookmark: true,
		applicationMode,
		isRovoLLMEnabled,
	});
	const { hasBookmarkCreationAccess, hasAdminhubAccess } = useAdminPermissions({
		cloudId: cloudId || '',
		orgId: orgId || '',
	});

	const userDetail = useMemo(() => ({ id: user?.id || '', name: user?.name || '' }), [user]);

	if (
		props.isRelayEnabled &&
		(props.resultsState === ResultsStates.NO_RESULTS || props.resultsState === ResultsStates.ERROR)
	) {
		return null;
	} else if (error && !results) {
		// Do not show smart answers when there are errors, or empty results
		return null;
	}
	// Do not show smart answers when the required data is not available
	if (!cloudId || !query) {
		return null;
	}

	const showBookmark = !bookmarkLoading && !isError && !selectedProduct && resultsLoaded;

	// If a bookmark exists & can be shown, prioritize showing bookmark only
	// If a bookmark doesn't exist & query isn't answerable by SAIN, show the bookmark component to admins
	if ((showBookmark && !!bookmarkUrl) || (showBookmark && hasBookmarkCreationAccess)) {
		return (
			<UFOSegment name="search-ai-dialog">
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="hasSAINComponent" value={true} />
						<UFOCustomCohortData dataKey="sainExperienceName" value="Bookmark" />
					</>
				) : (
					<UFOCustomData data={{ hasSAINComponent: true, sainExperienceName: 'Bookmark' }} />
				)}
				<Bookmark
					query={query}
					cloudId={cloudId}
					bookmarkId={bookmarkId}
					bookmarkState={bookmarkState}
					url={bookmarkUrl}
					orgId={orgId || ''}
					searchSessionId={searchSessionId}
					source={EVENT_SOURCE}
					brand={applicationMode === ApplicationModes.Rovo ? AiBrand.ROVO : AiBrand.AI}
					title={title || ''}
					description={description || ''}
					hasAdminhubAccess={hasAdminhubAccess}
					hasBookmarkCreationAccess={hasBookmarkCreationAccess}
					userDetails={userDetail}
					product={primaryProduct}
					workspaceId={workspaceId}
					lastModifierAccountId={lastModifierAccountId}
					keyPhrase={keyPhrase}
					parentAdminhubBookmarkId={parentAdminhubBookmarkId}
					requestId={bookmarkRequestId}
				/>
			</UFOSegment>
		);
	}

	return null;
};

const SAINIntercomWrapper = ({ children }: { children?: ReactNode }) => {
	const intercom = useSafeIntercom();
	const { queryIntent } = useQueryIntent();

	useEffect(() => {
		intercom?.trackEvent('smart_answers_shown', { query_intent: queryIntent });
	}, [intercom, queryIntent]);

	return <>{children}</>;
};

export const SmartAnswersIfEligible = (props: SmartAnswersProps): React.JSX.Element | null => {
	const [userWithStatus] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const [query] = useSearchQuery();
	const { isAdminHubAIEnabled } = useAppContext();
	const applicationMode = useApplicationMode();
	const areLLMFeaturesEnabled = useAreLLMPoweredFeaturesEnabled();
	const [{ cloudId }] = useBootstrap();

	const isUserAuthenticated = userWithStatus.status === UserStatus.Authenticated;
	const isPrimaryProductEligible =
		primaryProduct === ProductKeys.Confluence ||
		primaryProduct === ProductKeys.Atlas ||
		primaryProduct === ProductKeys.Jira;
	if (!isUserAuthenticated || !isPrimaryProductEligible || !query) {
		return null;
	}

	// Experiment to disable SAIN based on: https://atlassian.slack.com/archives/C06K9T53N66/p1757961269661659?thread_ts=1754004619.291609&cid=C06K9T53N66
	// skip hello users for SAIN holdback exp
	if (!isHello(cloudId) && fg('kd_disable_sain_experiment_fg')) {
		const layer = FeatureGates.getLayer(STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN);
		const abTestId = layer.get<string | null>('abTestId', 'no_ab_test_id');
		if (abTestId === 'kd_disable_sain_experiment') {
			const backendExperimentData = layer.get<{ sain: boolean } | null>('backendChanges', {
				sain: true,
			});
			if (!backendExperimentData?.sain) {
				return null;
			}
		}
	}

	if (fg('rovo_search_updated_entitlement_checks_for_team_25')) {
		if (areLLMFeaturesEnabled) {
			return (
				<UFOSegment name="search-page-smart-answers">
					<SmartAnswers {...props} />
				</UFOSegment>
			);
		}

		return (
			<UFOSegment name="search-page-smart-answers">
				<StandaloneBookmark {...props} />
			</UFOSegment>
		);
	} else {
		if (isAdminHubAIEnabled || applicationMode === ApplicationModes.Rovo) {
			return (
				<UFOSegment name="search-page-smart-answers">
					<SmartAnswers {...props} />
				</UFOSegment>
			);
		}
	}

	return null;
};

/**
 * A wrapper for the entire smart answers component in the search page,
 * which wraps the component in a div with a specific id. This is used
 * to calculate VC90 of the search page ignoring smart answers by filtering
 * out mutations that occur on descendants of this div during VC90 calculation.
 *
 * The specific metric this is used for is:
 * https://tools.atlassian.com/glance/experience/confluence.fe.page-load.ufo-search-page-ignoring-smart-answers
 */
const SmartAnswersWrapper = ({ children }: React.PropsWithChildren) => (
	<Box id="search-page-smart-answers" xcss={wrapperStyles.root}>
		{children}
	</Box>
);

const SmartAnswersWithErrorBoundary = (props: SmartAnswersProps): React.JSX.Element => (
	<ErrorBoundary section="SearchPageSmartAnswers">
		<SmartAnswersWrapper>
			{fg('kd_enable_sain_hero_nav') && (
				<Box
					xcss={cx(
						smartAnswersPortalStyles.root,
						// Hide the SAIN portal when the results state is not RESULTS
						// This is to prevent the SAIN portal from showing the previous nav card
						props.resultsState !== ResultsStates.RESULTS && smartAnswersPortalStyles.hidden,
					)}
					ref={props.smartAnswersPortalRef}
					testId="sain-nav-card-portal"
				/>
			)}
			<SmartAnswersIfEligible {...props} />
		</SmartAnswersWrapper>
	</ErrorBoundary>
);
export default SmartAnswersWithErrorBoundary;
